import React, { useState, useMemo } from 'react';
import { useStaff, useBranches } from '../db/hooks/useLookupData';
import { useDatabase } from '../db/DatabaseProvider';
import { useAuthStore } from '../stores';
import { useToast } from '../components/common/ToastProvider';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { FormatCurrency } from '../components/common/FormatCurrency';
import { StatusBadge } from '../components/common/StatusBadge';
import { DataTable, SyncStateIcon, type ColumnDef } from '../components/common/DataTable';
import { Button } from '../components/common/Button';
import { StaffFormDrawer } from '../components/staff/StaffFormDrawer';
import { WEEKDAYS } from '../components/staff/constants';
import { now } from '../lib/uuid';
import type { StaffDocType } from '../db/schemas';
import { 
  UserPlus, 
  Phone, 
  Mail, 
  Pencil, 
  Trash2, 
  Users,
  Building2,
  Briefcase,
} from 'lucide-react';

import { useDataTable } from '../hooks/useDataTable';

export function Staff() {
  const db = useDatabase();
  const user = useAuthStore((s) => s.user);
  const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
  const toast = useToast();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  const { staff, isLoading: loadingPersonal } = useStaff(selectedBranchId);
  const { branches } = useBranches();

  const tableState = useDataTable({
    data: staff,
    searchFields: ['name', 'role', 'phone', 'email'],
    initialSortBy: 'name',
    sortOptions: [
      { value: 'name', label: 'Nombre' },
      { value: 'role', label: 'Rol / Cargo' },
      { value: 'default_branch_id', label: 'Sucursal' },
      { value: 'is_active', label: 'Estado' },
    ],
    initialPageSize: '25',
    storageKey: 'depilaway-staff-page-size',
  });

  // Optimized O(1) Branch Lookup Map
  const branchesMap = useMemo(() => new Map(branches.map((s) => [s.id, s])), [branches]);

  // Drawer Form State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffDocType | null>(null);

  const openNew = () => {
    setEditingStaff(null);
    setIsFormOpen(true);
  };

  const openEdit = (staffMember: StaffDocType) => {
    setEditingStaff(staffMember);
    setIsFormOpen(true);
  };

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Miembro del Personal',
      message: '¿Está seguro de eliminar a este miembro del staff?',
      onConfirm: async () => {
        try {
          const doc = await db.staff.findOne(id).exec();
          if (doc) {
            await doc.patch({
              is_deleted: true,
              updated_at: now(),
              last_modified_by: user?.email || 'system',
            });
            toast.success('Miembro del staff eliminado correctamente');
          }
        } catch (err: any) {
          toast.error('Error al eliminar staff: ' + err.message);
        } finally {
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // Columns definition for Desktop Table View
  const columns: ColumnDef<StaffDocType>[] = [
    {
      key: 'name',
      header: 'Miembro',
      render: (staffMember) => {
        const salary = staffMember.monthly_salary;
        const frequency = staffMember.pay_frequency;
        return (
          <div>
            <div className="font-bold text-text-primary">{staffMember.name}</div>
            {salary ? (
              <div className="text-xs text-text-tertiary mt-0.5">
                Sueldo: <FormatCurrency amount={salary} /> ({frequency})
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'role',
      header: 'Rol',
      render: (staffMember) => (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-orange/10 text-brand-orange">
          <Briefcase className="h-3 w-3" />
          <span>{staffMember.role || 'Sin Rol'}</span>
        </span>
      ),
    },
    {
      key: 'work_days',
      header: 'Días Laborales',
      render: (staffMember) => (
        <div className="flex items-center gap-1">
          {WEEKDAYS.map((day) => {
            const days = (staffMember.work_days || {}) as Record<string, string>;
            const state = days?.[day.key] || days?.[day.fullLabel.toLowerCase()] || 'is_active';
            const tooltipText = `${day.fullLabel}: ${
              state === 'is_active' || state === 'active'
                ? 'Laboral'
                : state === 'libre' || state === 'off'
                ? 'Día Libre Remunerado'
                : 'No Laboral'
            }`;
            return (
              <span
                key={day.key}
                title={tooltipText}
                className={`w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold transition-all border cursor-help ${
                  state === 'is_active' || state === 'active'
                    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                    : state === 'libre' || state === 'off'
                    ? 'bg-brand-orange/10 text-brand-orange border-brand-orange/20'
                    : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                }`}
              >
                {day.label.charAt(0)}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      key: 'default_branch_id',
      header: 'Sucursal',
      sortable: true,
      render: (staffMember) => {
        const branchId = staffMember.default_branch_id;
        const branch = branchesMap.get(branchId);
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-sky/10 px-3 py-1 text-xs font-semibold text-brand-sky">
            <Building2 className="h-3.5 w-3.5" />
            <span>{branch ? branch.name : 'Sede Principal'}</span>
          </span>
        );
      },
    },
    {
      key: 'is_active',
      header: 'Estado',
      sortable: true,
      render: (staffMember) => <StatusBadge status={staffMember.is_active !== false ? 'active' : 'inactive'} />,
    },
    {
      key: 'acciones',
      header: 'Acciones',
      sortable: false,
      align: 'right',
      render: (staffMember) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="xs" icon={Pencil} onClick={() => openEdit(staffMember)} aria-label="Editar información" />
          <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDelete(staffMember.id)} aria-label="Eliminar staff" />
        </div>
      ),
    },
  ];

  // Custom Render for Mobile Cards (<768px)
  const renderMobileCard = (staffMember: StaffDocType) => {
    const branchId = staffMember.default_branch_id;
    const branch = branchesMap.get(branchId || '');
    const cleanPhone = staffMember.phone?.replace(/\D/g, '');
    const salary = staffMember.monthly_salary;
    const frequency = staffMember.pay_frequency;
    const roleName = staffMember.role;

    return (
      <div className="p-4 rounded-2xl border border-border bg-surface shadow-xs space-y-3 hover:border-brand-orange/40 transition-all">
        {/* Header: Avatar / Name / Role / Branch / Sync */}
        <div className="flex items-start justify-between gap-2 border-b border-border/40 pb-2.5">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-brand-orange/10 text-brand-orange font-extrabold flex items-center justify-center text-sm border border-brand-orange/20">
                {staffMember.name.charAt(0).toUpperCase()}
              </div>
              <span
                className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-surface ${
                  staffMember.is_active !== false ? 'bg-emerald-500' : 'bg-slate-400'
                }`}
              />
            </div>
            <div>
              <h3 className="font-bold text-base text-text-primary leading-snug">{staffMember.name}</h3>
              {roleName && (
                <span className="inline-flex px-2 py-0.5 rounded-md bg-brand-sky/10 text-brand-sky font-semibold text-[11px] mt-0.5">
                  {roleName}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SyncStateIcon state={(staffMember as any).sync_state} />
            <span className="inline-flex items-center gap-1 rounded-full bg-surface-tertiary px-2.5 py-0.5 text-[11px] font-semibold text-text-secondary border border-border">
              <Building2 className="h-3 w-3" />
              <span>{branch ? branch.name : 'Sede Principal'}</span>
            </span>
          </div>
        </div>

        {/* Salary & Payment Frequency Breakdown */}
        <div className="grid grid-cols-2 gap-2 text-xs p-2.5 rounded-xl bg-surface-tertiary/50 border border-border/30">
          <div>
            <span className="text-text-tertiary font-medium block text-[10px] uppercase">Sueldo Base:</span>
            {salary ? (
              <span className="font-bold text-text-primary">
                <FormatCurrency amount={salary} />
              </span>
            ) : (
              <span className="text-text-tertiary italic">No definido</span>
            )}
          </div>
          <div>
            <span className="text-text-tertiary font-medium block text-[10px] uppercase">Frecuencia Pago:</span>
            <span className="font-bold text-text-primary capitalize">
              {frequency || 'Semanal'}
            </span>
          </div>
        </div>

        {/* Weekly Schedule Chips */}
        <div>
          <span className="text-[10px] uppercase font-bold text-text-tertiary block mb-1.5">
            Horario Semanal:
          </span>
          <div className="flex items-center gap-1">
            {WEEKDAYS.map((day) => {
              const days = (staffMember.work_days || {}) as Record<string, string>;
              const state = days?.[day.key] || days?.[day.fullLabel.toLowerCase()] || 'is_active';
              return (
                <span
                  key={day.key}
                  title={`${day.fullLabel}: ${state}`}
                  className={`w-7 h-7 flex items-center justify-center rounded-xl text-[11px] font-bold transition-all border ${
                    state === 'is_active'
                      ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                      : state === 'libre'
                      ? 'bg-brand-orange/10 text-brand-orange border-brand-orange/20'
                      : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                  }`}
                >
                  {day.label.charAt(0)}
                </span>
              );
            })}
          </div>
        </div>

        {/* Contact & Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-border/30">
          <div className="flex items-center gap-2">
            {cleanPhone && (
              <Button
                as="a"
                href={`https://wa.me/${cleanPhone}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="success"
                size="xs"
                icon={Phone}
                aria-label="Llamar / WhatsApp"
              />
            )}
            {staffMember.email && (
              <Button
                as="a"
                href={`mailto:${staffMember.email}`}
                variant="secondary"
                size="xs"
                icon={Mail}
                aria-label="Enviar correo"
              />
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" icon={Pencil} onClick={() => openEdit(staffMember)}>
              Editar
            </Button>
            <Button variant="danger" size="sm" icon={Trash2} onClick={() => handleDelete(staffMember.id)}>
              Eliminar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-text-primary tracking-tight">Personal & Operadores</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Gestión de roles, sueldos, branches y horarios del equipo de trabajo
          </p>
        </div>
        <Button variant="primary" size="md" icon={UserPlus} onClick={openNew} className="self-start sm:self-auto">
          Nuevo Miembro
        </Button>
      </div>

      {/* Unified DataTable Component */}
      <DataTable<StaffDocType>
        data={staff}
        columns={columns}
        getItemKey={(item) => item.id}
        getSyncState={(item) => (item as any).sync_state}
        tableState={tableState}
        isLoading={loadingPersonal}
        renderMobileCard={renderMobileCard}
        searchPlaceholder="Buscar por name, rol o teléfono..."
        emptyTitle="No hay miembros del equipo registrados"
        emptyDescription="Agrega a los operadores y staff de cada sucursal para asignar services y comisiones."
        emptyIcon={Users}
        actionButton={
          <Button variant="primary" size="sm" icon={UserPlus} onClick={openNew}>
            Registrar Primer Miembro
          </Button>
        }
      />

      {/* Reusable Drawer for Add/Edit Staff Form */}
      <StaffFormDrawer
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingStaff(null);
        }}
        staffToEdit={editingStaff}
        onSuccess={() => {
          setIsFormOpen(false);
          setEditingStaff(null);
        }}
      />

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Eliminar"
      />
    </div>
  );
}
